import { supabase } from './supabase';
import { createPayment } from './stripe';
import { getSystemPrompt } from './gpt/prompts';
import type { AIAnalysisResponse, Entry } from './types';
import { Language } from './i18n/config';
import OpenAI from 'openai';

const openai = new OpenAI({
  apiKey: import.meta.env.VITE_OPENAI_API_KEY,
  dangerouslyAllowBrowser: true
});

const isBetaPhase = import.meta.env.VITE_BETA_PHASE === 'true';

export async function requestAIAnalysis(sessionId: string, entries: Entry[], forceNew: boolean = false): Promise<AIAnalysisResponse> {
  try {
    // First, check if a summary exists at all
    const { data: existingSummary, error: summaryError } = await supabase
      .from('ai_summaries')
      .select('*')
      .eq('session_id', sessionId)
      .maybeSingle();

    if (summaryError) throw summaryError;

    // Get session details for the topic and language
    const { data: session, error: sessionError } = await supabase
      .from('sessions')
      .select('topic, language')
      .eq('id', sessionId)
      .single();

    if (sessionError) throw sessionError;
    if (!session) throw new Error('Session not found');

    const lang = (session.language || 'de') as Language;
    console.log(`API: Requesting analysis for session ${sessionId} with language: ${lang}`);

    // If we have a completed summary and don't want to force new, return it
    if (!forceNew && existingSummary?.payment_status === 'completed') {
      return {
        success: true,
        summary: existingSummary
      };
    }

    // If not in beta phase, handle payment flow
    if (!isBetaPhase) {
      try {
        // Update or create a pending summary
        const { data: pendingSummary, error: upsertError } = await supabase
          .from('ai_summaries')
          .upsert({
            id: existingSummary?.id, // Include ID if updating
            session_id: sessionId,
            content: '',
            payment_status: 'pending'
          })
          .select()
          .single();

        if (upsertError) throw upsertError;

        // Create Stripe payment with language parameter
        const paymentResult = await createPayment(sessionId, lang);
        if (!paymentResult.success) {
          throw new Error(paymentResult.error || 'Payment failed');
        }

        return {
          success: true,
          payment_url: paymentResult.paymentUrl
        };
      } catch (error) {
        console.error('Payment process error:', error);
        throw error;
      }
    }

    // Beta phase: Generate analysis directly
    // Update existing summary or create new one
    const { data: summary, error: upsertError } = await supabase
      .from('ai_summaries')
      .upsert({
        id: existingSummary?.id, // Include ID if updating
        session_id: sessionId,
        content: '',
        payment_status: 'processing'
      })
      .select()
      .single();

    if (upsertError) throw upsertError;

    // Group entries by letter for better analysis
    const entriesByLetter = entries.reduce((acc, entry) => {
      const letter = entry.letter.toUpperCase();
      if (!acc[letter]) acc[letter] = [];
      acc[letter].push(entry.content);
      return acc;
    }, {} as Record<string, string[]>);

    console.log(`API: Generating beta phase analysis in language: ${lang}`);

    // Generate analysis with OpenAI
    const completion = await openai.chat.completions.create({
      model: "gpt-4o-mini",
      messages: [
        {
          role: "system",
          content: getSystemPrompt(lang)
        },
        {
          role: "user",
          content: `${lang === 'de' ? 'Analysiere' : 'Analyze'} ${
            lang === 'de' ? 'diese Brainstorming-Ergebnisse' : 'these brainstorming results'
          } ${lang === 'de' ? 'zum Thema' : 'for the topic'} "${session.topic}":\n\n${
            Object.entries(entriesByLetter)
              .sort(([a], [b]) => a.localeCompare(b))
              .map(([letter, words]) => `${letter}:\n${words.join(', ')}`)
              .join('\n\n')
          }\n\n${
            lang === 'de'
              ? `Berücksichtige bei deiner Analyse, dass das Brainstorming-Thema "${session.topic}" ist. WICHTIG: Deine Antwort MUSS auf Deutsch sein.`
              : `Consider in your analysis that the brainstorming topic is "${session.topic}".`
          }`
        }
      ],
      temperature: 0.7,
      max_tokens: 1000
    });


    const analysis = completion.choices[0]?.message?.content || 
      (lang === 'de' ? 'Keine Analyse verfügbar.' : 'No analysis available.');

    // Update the summary with the analysis
    const { error: updateError } = await supabase
      .from('ai_summaries')
      .update({
        content: analysis,
        payment_status: 'completed'
      })
      .eq('id', summary.id);

    if (updateError) throw updateError;

    return {
      success: true,
      summary: {
        ...summary,
        content: analysis,
        payment_status: 'completed'
      }
    };
  } catch (error) {
    console.error('Error in AI analysis:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Failed to generate AI analysis'
    };
  }
}